// frontend/src/pages/About.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService, getFullImageUrl } from '../services/api';
import SEOHead from '../components/SEOHead';
import { 
  Users, 
  Target, 
  Eye, 
  Award, 
  Heart, 
  Shield,
  Clock,
  TrendingUp,
  Star,
  CheckCircle,
  ArrowLeft
} from 'lucide-react';

const About = () => {
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('about');

  useEffect(() => {
    fetchAboutData();
  }, []);

  const fetchAboutData = async () => {
    try {
      const response = await apiService.getAboutData();
      setAboutData(response.data);
    } catch (error) {
      console.error('Error fetching about data:', error);
    } finally {
      setLoading(false);
    }
  };

  // آیکون‌های پیشفرض برای ارزش‌ها
  const getIconComponent = (iconName) => {
    const icons = {
      'users': Users,
      'target': Target,
      'eye': Eye,
      'award': Award,
      'heart': Heart,
      'shield': Shield,
      'clock': Clock,
      'trending-up': TrendingUp,
      'star': Star,
      'check-circle': CheckCircle,
    };
    
    const IconComponent = icons[iconName] || Award;
    return <IconComponent className="w-6 h-6" />;
  };

  // آمار شرکت
  const companyStats = [
    { number: '۱۰+', label: 'سال تجربه', icon: Clock },
    { number: '۵۰۰+', label: 'پروژه موفق', icon: Award },
    { number: '۱٬۰۰۰+', label: 'مشتری راضی', icon: Users },
    { number: '۱۰+', label: 'کارشناس متخصص', icon: Star },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  if (!aboutData) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Users className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">اطلاعاتی یافت نشد</h2>
          <p className="text-gray-400 mb-4">صفحه درباره ما در حال حاضر در دسترس نیست.</p>
          <Link to="/" className="text-yellow-400 hover:text-yellow-300">
            بازگشت به خانه
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead 
        title={`درباره ما - ${aboutData.title}`}
        description={aboutData.subtitle}
        keywords="طراحی و ساخت انواع قالب، تولید قطعات پلاستیکی ،قالب سازی، لاستیک سازی، اورینگ، سیلیکون، پکینگ، دیافراگم، واشر"
        image={getFullImageUrl(aboutData.main_image)}
        type="website"
      />
      
      <div className="min-h-screen bg-black">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-gray-900 to-black">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Content */}
              <div className="text-white space-y-6">
                <h1 className="text-4xl md:text-5xl font-bold leading-tight">
                  {aboutData.title}
                </h1>
                <p className="text-xl text-gray-300 leading-relaxed">
                  {aboutData.subtitle}
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link
                    to="/contact"
                    className="bg-yellow-400 text-gray-900 px-6 py-3 rounded-lg font-bold hover:bg-yellow-300 transition-all"
                  >
                    تماس با ما
                  </Link>
                  <Link
                    to="/products"
                    className="border-2 border-white text-white px-6 py-3 rounded-lg font-bold hover:bg-white hover:text-gray-900 transition-all"
                  >
                    مشاهده محصولات
                  </Link>
                </div>
              </div>
              
              {/* Image */}
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden">
                  <img
                    src={getFullImageUrl(aboutData.main_image)}
                    alt={aboutData.title}
                    className="w-full h-96 object-cover rounded-2xl"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-40 rounded-2xl"></div>
                </div>
                
                {/* Experience Badge */}
                <div className="absolute -top-4 -right-4 bg-yellow-400 text-gray-900 px-6 py-3 rounded-2xl shadow-lg">
                  <div className="text-2xl font-bold">۱۰+ سال</div>
                  <div className="text-sm font-medium">تجربه درخشان</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Statistics */}
        {aboutData.show_statistics && (
          <section className="py-16 bg-gray-900">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {companyStats.map((stat, index) => {
                  const Icon = stat.icon;
                  return (
                    <div key={index} className="text-center text-white">
                      <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700 hover:border-yellow-400 transition-all">
                        <Icon className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
                        <div className="text-3xl font-bold text-yellow-400 mb-1">
                          {stat.number}
                        </div>
                        <div className="text-gray-300 text-sm">
                          {stat.label}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Main Content */}
        <section className="py-16 bg-black">
          <div className="container mx-auto px-4">
            {/* Tab Navigation */}
            <div className="flex justify-center mb-12">
              <div className="bg-gray-900 rounded-2xl p-2 border border-gray-800">
                {[
                  { id: 'about', label: 'درباره ما' },
                  { id: 'mission', label: 'ماموریت' },
                  { id: 'vision', label: 'چشم انداز' },
                  { id: 'values', label: 'ارزش‌ها' },
                  { id: 'team', label: 'تیم ما' },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-6 py-3 rounded-xl font-medium transition-all ${
                      activeTab === tab.id
                        ? 'bg-yellow-400 text-gray-900'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="max-w-4xl mx-auto">
              {activeTab === 'about' && (
                <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
                  <div 
                    className="prose prose-invert max-w-none text-gray-300 leading-8"
                    dangerouslySetInnerHTML={{ __html: aboutData.main_content }}
                  />
                </div>
              )}

              {activeTab === 'mission' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
                    <div className="flex items-center space-x-3 space-x-reverse mb-6">
                      <Target className="w-8 h-8 text-yellow-400" />
                      <h2 className="text-2xl font-bold text-white">
                        {aboutData.mission_title}
                      </h2>
                    </div>
                    <div 
                      className="prose prose-invert max-w-none text-gray-300 leading-8"
                      dangerouslySetInnerHTML={{ __html: aboutData.mission_content }}
                    />
                  </div>
                  
                  {aboutData.mission_image && (
                    <div className="relative rounded-2xl overflow-hidden">
                      <img
                        src={getFullImageUrl(aboutData.mission_image)}
                        alt={aboutData.mission_title}
                        className="w-full h-64 lg:h-full object-cover rounded-2xl"
                      />
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'vision' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {aboutData.vision_image && (
                    <div className="relative rounded-2xl overflow-hidden">
                      <img
                        src={getFullImageUrl(aboutData.vision_image)}
                        alt={aboutData.vision_title}
                        className="w-full h-64 lg:h-full object-cover rounded-2xl"
                      />
                    </div>
                  )}
                  
                  <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
                    <div className="flex items-center space-x-3 space-x-reverse mb-6">
                      <Eye className="w-8 h-8 text-yellow-400" />
                      <h2 className="text-2xl font-bold text-white">
                        {aboutData.vision_title}
                      </h2>
                    </div>
                    <div 
                      className="prose prose-invert max-w-none text-gray-300 leading-8"
                      dangerouslySetInnerHTML={{ __html: aboutData.vision_content }}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'values' && (
                <div className="space-y-8">
                  <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
                    <h2 className="text-2xl font-bold text-white mb-6">
                      {aboutData.values_title}
                    </h2>
                    <div 
                      className="prose prose-invert max-w-none text-gray-300 leading-8 mb-8"
                      dangerouslySetInnerHTML={{ __html: aboutData.values_content }}
                    />
                  </div>

                  {/* Company Values Grid */}
                  {aboutData.values && aboutData.values.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {aboutData.values.map((value, index) => (
                        <div key={value.id} className="bg-gray-800 rounded-2xl p-6 border border-gray-700 hover:border-yellow-400 transition-all group">
                          <div className="flex items-center space-x-3 space-x-reverse mb-4">
                            <div className="p-2 bg-yellow-400 text-gray-900 rounded-lg group-hover:scale-110 transition-transform">
                              {getIconComponent(value.icon)}
                            </div>
                            <h3 className="text-lg font-bold text-white">
                              {value.title}
                            </h3>
                          </div>
                          <p className="text-gray-300 text-sm leading-relaxed">
                            {value.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'team' && (
                <div className="space-y-8">
                  <div className="text-center">
                    <h2 className="text-3xl font-bold text-white mb-4">تیم متخصصان ما</h2>
                    <p className="text-gray-400 max-w-2xl mx-auto">
                      با تیم متخصص و با تجربه BusinessPro آشنا شوید
                    </p>
                  </div>

                  {aboutData.team_members && aboutData.team_members.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {aboutData.team_members.map((member) => (
                        <div key={member.id} className="bg-gray-900 rounded-2xl overflow-hidden border border-gray-800 hover:border-yellow-400 transition-all group">
                          <div className="relative h-48 overflow-hidden">
                            <img
                              src={getFullImageUrl(member.image)}
                              alt={member.name}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-60"></div>
                          </div>
                          
                          <div className="p-6">
                            <h3 className="text-xl font-bold text-white mb-2 group-hover:text-yellow-400 transition-colors">
                              {member.name}
                            </h3>
                            <p className="text-yellow-400 font-medium mb-3">
                              {member.position}
                            </p>
                            <p className="text-gray-300 text-sm leading-relaxed line-clamp-3">
                              {member.bio}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-gray-900 rounded-2xl border border-gray-800">
                      <Users className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400">اطلاعات تیم به زودی اضافه خواهد شد.</p>
                      <Link
                        to="/portfolio"
                        className="inline-flex items-center space-x-2 space-x-reverse text-yellow-400 hover:text-yellow-300 mt-4"
                      >
                        <span>مشاهده پورتفولیو تیم</span>
                        <ArrowLeft size={16} />
                      </Link>
                    </div>
                  )}

                  <div className="text-center">
                    <Link
                      to="/portfolio"
                      className="inline-flex items-center space-x-2 space-x-reverse bg-yellow-400 text-gray-900 px-6 py-3 rounded-lg font-bold hover:bg-yellow-300 transition-all"
                    >
                      <span>مشاهده کامل تیم</span>
                      <ArrowLeft size={20} />
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-yellow-400 to-yellow-600">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              آماده همکاری با ما هستید؟
            </h2>
            <p className="text-gray-800 text-xl mb-8 max-w-2xl mx-auto">
              برای دریافت مشاوره رایگان و اطلاعات بیشتر درباره محصولات و خدمات ما، با ما در ارتباط باشید.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-gray-900 text-white px-8 py-4 rounded-full font-bold hover:bg-gray-800 transition-all transform hover:scale-105"
              >
                📞 تماس با ما
              </Link>
              <Link
                to="/products"
                className="border-2 border-gray-900 text-gray-900 px-8 py-4 rounded-full font-bold hover:bg-gray-900 hover:text-white transition-all transform hover:scale-105"
              >
                🛒 مشاهده محصولات
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default About;